// Readable names for codes, from the active config pack: the field name of a field id (panel and set members
// included) and the criterion of a rule. Loaded once per page view; a code without a name is shown as itself.
import { useEffect, useState } from 'react';
import { activePack } from './data.ts';
import { fieldDef, type FieldDef } from './template.ts';

interface Names {
  field: (fid: string) => string;
  rule: (rid: string) => string;
}

let cache: Promise<{ fields: FieldDef[]; criteria: Map<string, string> }> | null = null;

function load() {
  cache ??= activePack().then((p) => {
    const pack = p?.pack as unknown as
      | { fields: { items: FieldDef[] }; rules: { items: { rule_id: string; criterion: string }[] } }
      | undefined;
    return {
      fields: pack?.fields.items ?? [],
      criteria: new Map((pack?.rules.items ?? []).map((r) => [r.rule_id, r.criterion])),
    };
  });
  return cache;
}

export function fieldName(fields: FieldDef[], fid: string): string {
  const def = fieldDef(fields, fid);
  if (!def) return fid;
  return def.field_id === fid ? def.field_name : `${def.field_name}: ${fid.split(/[._]/).pop() ?? ''}`;
}

export function useNames(): Names {
  const [data, setData] = useState<{ fields: FieldDef[]; criteria: Map<string, string> } | null>(null);
  useEffect(() => {
    void load()
      .then(setData)
      .catch(() => {
        cache = null;
      });
  }, []);
  return {
    field: (fid) => (data ? fieldName(data.fields, fid) : fid),
    rule: (rid) => data?.criteria.get(rid) ?? '',
  };
}
